// @flow
import _ from 'lodash';
import Job from './Job';
import PollJob from './PollJob';
import {injectIframe} from '../util/DOMUtil';
import {RepoItem} from '../autogenerated/ZoomBridge';
import HashMap from './HashMap';
import PreviewHelper from '../helper/PreviewHelper';
import ThumbnailUtil from '../util/ThumbnailUtil';
import DownloadArgsHelper from '../helper/DownloadArgsHelper';
import Store from '../store/Store';
import {DOWNLOAD_JOBS} from '../store/Constants';
import FileInfoUtil from '../util/FileInfoUtil';
import Http from '../http/Http';
import ConnectionUtil from "../util/session/ConnectionUtil";

const debug = require('../debug/debug')('wc:protocol:PollAndDownloadJob');

export default class PollAndDownloadJob extends Job {
  _downloadUrl: string;
  _pollJob: PollJob;

  constructor(downloadUrl: string, pollUrl: string, predicate: Function, config: ?Object) {
    super();

    this._downloadUrl = downloadUrl;

    this._promise = new Promise(async (resolve, reject) => {
      try {
        debug('creating PollJob');
        this._pollJob = new PollJob(pollUrl, predicate, config);
        await this._pollJob.promise;

        if (this._canceled) {
          throw new Error('Canceled');
        }

        injectIframe(downloadUrl);
        debug('job success');
        this._done = true;
        Store.put(DOWNLOAD_JOBS, Store.get(DOWNLOAD_JOBS).concat(this));
        resolve();
      } catch (err) {
        debug('job error: ', err);
        this._done = true;
        Store.put(DOWNLOAD_JOBS, Store.get(DOWNLOAD_JOBS).concat(this));
        reject(err);
      }
    });

    // Should we be adding jobs to the store here in the constructor or in some manager?
    Store.put(DOWNLOAD_JOBS, Store.get(DOWNLOAD_JOBS).concat(this));
  }

  get description() {
    const desc = super.description;
    return {
      label: `Download Task (${desc.status})`,
      tooltip: `URL: ${this._downloadUrl}`,
      status: desc.status
    };
  }

  cancel() {
    debug('[cancel()]');
    this._canceled = true;
    this._pollJob.cancel();
  }

  static async create(assets: RepoItem[], fuid2ArgsHelperMap: ?HashMap, resolution: ?number, pageNumber: ?number, frn: ?number, rrn: ?number) {
    if (_.some(assets, (a) => FileInfoUtil.isFolder(a))) {
      return PollAndDownloadJob.createForFolders(assets);
    }

    if (!fuid2ArgsHelperMap) {
      fuid2ArgsHelperMap = new HashMap();
      assets.forEach((asset) => {
        const downloadArgsHelper = new DownloadArgsHelper();
        if (frn) {
          downloadArgsHelper.setFrn(frn);
        }
        if (rrn) {
          downloadArgsHelper.setRrn(rrn);
        }
        fuid2ArgsHelperMap.put(asset.getFuid(), downloadArgsHelper);
      });
    }

    debug('[create()] args: ', {assets, fuid2ArgsHelperMap, resolution, pageNumber});
    const res = await PreviewHelper.loadJobId(assets, fuid2ArgsHelperMap, resolution, pageNumber);
    if (!res.success) {
      throw new Error(res.message);
      //throw new Error('Server failed to start download');
    }

    const jobId = res.entries[0][0];
    debug('[create()] got job id: ', jobId);

    debug('[create()] creating PollAndDownloadJob');
    const downloadUrl = `${ThumbnailUtil.downloadProgressUrl(jobId)}&forceDownloadAsAttachment=true`;
    const pollUrl = ThumbnailUtil.createJobProgressInfoUrl(jobId);

    const predicate = (data) => {
      if (data.jobStatus !== 'finished') {
        return false;
      }

      if (!data.jobSuccess) {
        throw new Error(data.msg);
      }

      return true;
    };

    return new PollAndDownloadJob(downloadUrl, pollUrl, predicate);
  }

  static async createForFolders(assets: RepoItem[]) {
    debug('[createForFolders()] args: ', {assets});
    const url = ThumbnailUtil.getDownloadFolderUrl(assets);
    const data = await Http.get(url);
    if (!data.success) {
      if(data.error) {
        throw new Error(data.error)
      } else {
        throw new Error('Server failed to start download');
      }  
    }

    const jobId = data.id;
    debug('[createForFolders()] got job id: ', jobId);

    debug('[createForFolders()] creating PollAndDownloadJob');
    const downloadUrl = `${ConnectionUtil.getPreviewServerUrl()}/review/api/download/${jobId}`;
    const pollUrl = `${ConnectionUtil.getPreviewServerUrl()}/review/api/download/${jobId}/poll`;

    const predicate = (data) => {
      if (data.status !== 'done') {
        return false;
      }

      if (!data.success) {
        throw new Error(data.msg);
      }

      return true;
    };

    return new PollAndDownloadJob(downloadUrl, pollUrl, predicate);
  }
}
